/**
 * Audit & Logs - Wave 7 (2026-05-27).
 *
 * Unified viewer for the five append-only audit logs that previously
 * had no UI surface even though their tables / static catalogues
 * shipped weeks ago:
 *
 *   1. notify_send_log         - every email + SMS dispatch attempt
 *   2. tenant_bootstrap_log    - cross-tenant bootstrap audit
 *   3. inbound_events          - webhook ingest log (counterpart to EgressLog)
 *   4. Edge scanner-capability matrix - per-component capability truth table
 *   5. Tier 4 exemplar catalogue - static pgvector reference values
 *
 * Tabbed because together they form the "what does the platform
 * actually do under the hood?" audit surface BFSI CISOs ask for.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useMotionPreset } from '@/lib/motion-level';
import {
  Mail,
  Sparkles,
  Inbox,
  Cpu,
  Hash,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { LoadingState, ErrorState } from '@foctta/ui-shell';
import { EmptyStateV2 } from '@/components/ui/empty-state-v2';
import {
  listNotifySendLog,
  listBootstrapLog,
  listInboundEvents,
  getEdgeScannerCapabilities,
  listTier4Exemplars,
  type NotifySendLogEntry,
  type BootstrapLogEntry,
  type InboundEventEntry,
  type EdgeScannerCapabilities,
  type Tier4ExemplarSummary,
} from '@/lib/api';
import { cn } from '@/lib/cn';

type TabKey = 'sends' | 'bootstrap' | 'inbound' | 'edge' | 'exemplars';

const TABS: Array<{ key: TabKey; label: string; icon: typeof Mail }> = [
  { key: 'sends', label: 'Notify Send Log', icon: Mail },
  { key: 'bootstrap', label: 'Bootstrap Log', icon: Sparkles },
  { key: 'inbound', label: 'Inbound Events', icon: Inbox },
  { key: 'edge', label: 'Edge Capabilities', icon: Cpu },
  { key: 'exemplars', label: 'Tier 4 Exemplars', icon: Hash },
];

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'critical' | 'glass'> = {
  delivered: 'success',
  delivered_confirmed: 'success',
  sent: 'success',
  processed: 'success',
  succeeded: 'success',
  received: 'glass',
  queued: 'glass',
  processing: 'warning',
  partial: 'warning',
  ignored: 'glass',
  bounced: 'critical',
  complained: 'critical',
  failed: 'critical',
};

function tsAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function AuditLogsPage(): JSX.Element {
  const [tab, setTab] = useState<TabKey>('sends');
  const { level } = useMotionPreset();
  const swapTransition = level === 'off'
    ? { duration: 0 }
    : level === 'reduced'
      ? { duration: 0.14 }
      : { type: 'spring' as const, stiffness: 360, damping: 30 };

  return (
    <PageContainer>
      <PageHeader
        title="Audit & Logs"
        subtitle="Append-only operational logs and capability registries - regulator-grade evidence of what the platform sends, ingests, bootstraps, and supports."
        badges={[
          { label: 'Append-only', variant: 'glass' },
          { label: 'SEBI / DPDPA evidence', variant: 'info' },
        ]}
      />

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors',
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'glass-lite hover:bg-muted/30',
              )}
            >
              <Icon className="h-3 w-3" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Wave 8 (2026-05-27) - cross-fade between tabs. mode="wait"
          ensures the outgoing panel exits before the incoming mounts,
          so the page doesn't briefly show both. */}
      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={level === 'full' ? { opacity: 0, y: 4 } : { opacity: 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={level === 'full' ? { opacity: 0, y: -4 } : { opacity: 0 }}
          transition={swapTransition}
        >
          {tab === 'sends' && <NotifySendLogPanel />}
          {tab === 'bootstrap' && <BootstrapLogPanel />}
          {tab === 'inbound' && <InboundEventsPanel />}
          {tab === 'edge' && <EdgeCapabilitiesPanel />}
          {tab === 'exemplars' && <Tier4ExemplarsPanel />}
        </motion.div>
      </AnimatePresence>
    </PageContainer>
  );
}

// ─── Tab 1: notify_send_log ──────────────────────────────────────────

function NotifySendLogPanel(): JSX.Element {
  const [entries, setEntries] = useState<NotifySendLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listNotifySendLog({ limit: 100 });
      setEntries(r.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  if (loading) return <LoadingState caption="Loading send log…" />;
  if (error) return <ErrorState title="Failed to load" detail={error} onRetry={() => void refresh()} />;
  if (entries.length === 0)
    return (
      <EmptyStateV2
        icon={Mail}
        title="No send attempts on record"
        description="When the email or SMS dispatcher sends a notification, every attempt lands here with provider id, status, and any error message. The table is append-only - SEBI CSCRF evidence."
      />
    );

  return (
    <GlassCard className="p-0 overflow-hidden">
      <div className="flex items-center justify-end px-4 py-2 border-b border-border/40">
        <Button size="sm" variant="glass" onClick={() => void refresh()}>
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      </div>
      <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
        <div className="col-span-1">Ch</div>
        <div className="col-span-2">To</div>
        <div className="col-span-2">Subject / Template</div>
        <div className="col-span-2">Provider</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1 text-center">Attempt</div>
        <div className="col-span-2 text-right">When</div>
      </div>
      {entries.map((e, i) => (
        <motion.div
          key={e.id}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.02, 0.2) }}
          className="grid grid-cols-12 px-4 py-2.5 text-xs border-b border-border/30 hover:bg-muted/20 items-center"
        >
          <div className="col-span-1">
            <Badge variant="glass" className="text-[9px] uppercase">
              {e.channel}
            </Badge>
          </div>
          <div className="col-span-2 truncate" title={e.toAddress}>
            <code className="font-mono text-[10px]">{e.toAddress}</code>
          </div>
          <div className="col-span-2 truncate text-muted-foreground">
            {e.subject ?? e.templateKey ?? '-'}
          </div>
          <div className="col-span-2 truncate">
            <code className="font-mono text-[10px]">{e.providerKind}</code>
          </div>
          <div className="col-span-2">
            <Badge variant={STATUS_VARIANT[e.status] ?? 'glass'} className="text-[9px]">
              {e.status}
            </Badge>
            {e.errorMessage && (
              <span className="ml-1 text-[10px] text-destructive truncate" title={e.errorMessage}>
                · err
              </span>
            )}
          </div>
          <div className="col-span-1 text-center tabular">{e.attemptNumber}</div>
          <div className="col-span-2 text-right text-[10px] text-muted-foreground" title={e.createdAt}>
            {tsAgo(e.createdAt)}
          </div>
        </motion.div>
      ))}
    </GlassCard>
  );
}

// ─── Tab 2: tenant_bootstrap_log ─────────────────────────────────────

function BootstrapLogPanel(): JSX.Element {
  const [entries, setEntries] = useState<BootstrapLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listBootstrapLog({ limit: 100 });
      setEntries(r.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  if (loading) return <LoadingState caption="Loading bootstrap log…" />;
  if (error) return <ErrorState title="Failed to load" detail={error} onRetry={() => void refresh()} />;
  if (entries.length === 0)
    return (
      <EmptyStateV2
        icon={Sparkles}
        title="No bootstrap events recorded"
        description="Cross-tenant bootstrap events from Core land here - discovery_enabled, business-unit-sync, evidence-pack-ready. Idempotent via event_id."
      />
    );

  return (
    <div className="space-y-2">
      {entries.map((e, i) => {
        const StatusIcon =
          e.status === 'completed' || e.status === 'processed'
            ? CheckCircle2
            : e.status === 'failed'
              ? XCircle
              : e.status === 'partial'
                ? AlertTriangle
                : ChevronRight;
        return (
          <motion.div
            key={e.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.02, 0.2) }}
          >
            <GlassCard className="p-3 text-xs">
              <div className="flex items-start gap-3">
                <StatusIcon
                  className={cn(
                    'h-4 w-4 mt-0.5 shrink-0',
                    e.status === 'completed' || e.status === 'processed'
                      ? 'text-success'
                      : e.status === 'failed'
                        ? 'text-destructive'
                        : e.status === 'partial'
                          ? 'text-warning'
                          : 'text-muted-foreground',
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <code className="font-mono text-[11px]">{e.eventType}</code>
                    <Badge variant={STATUS_VARIANT[e.status] ?? 'glass'} className="text-[9px]">
                      {e.status}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">{tsAgo(e.receivedAt)}</span>
                    {e.coreTenantId && (
                      <span className="text-[10px] text-muted-foreground">
                        core: <code className="font-mono">{e.coreTenantId.slice(0, 8)}…</code>
                      </span>
                    )}
                  </div>
                  <code className="text-[10px] font-mono text-muted-foreground block mt-0.5">
                    event_id: {e.eventId}
                  </code>
                  {e.errorDetail && (
                    <p className="text-[11px] text-destructive mt-1">{e.errorDetail}</p>
                  )}
                  {Array.isArray(e.stepResults) && e.stepResults.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-muted-foreground cursor-pointer">
                        {e.stepResults.length} step result(s)
                      </summary>
                      <pre className="text-[10px] font-mono glass-lite p-2 rounded mt-1 overflow-x-auto">
                        {JSON.stringify(e.stepResults, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </GlassCard>
          </motion.div>
        );
      })}
    </div>
  );
}

// ─── Tab 3: inbound_events ───────────────────────────────────────────

function InboundEventsPanel(): JSX.Element {
  const [entries, setEntries] = useState<InboundEventEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listInboundEvents({ limit: 100 });
      setEntries(r.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  if (loading) return <LoadingState caption="Loading inbound events…" />;
  if (error) return <ErrorState title="Failed to load" detail={error} onRetry={() => void refresh()} />;
  if (entries.length === 0)
    return (
      <EmptyStateV2
        icon={Inbox}
        title="No inbound events yet"
        description="Webhook events from registered connectors (Core, OneTrust, Securiti, TrustArc, custom) land here for audit. Counterpart to the Egress Log."
      />
    );

  return (
    <GlassCard className="p-0 overflow-hidden">
      <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
        <div className="col-span-2">Category</div>
        <div className="col-span-3">Event type</div>
        <div className="col-span-1">Sev</div>
        <div className="col-span-2">Connector</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2 text-right">When</div>
      </div>
      {entries.map((e, i) => (
        <motion.div
          key={e.id}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.02, 0.2) }}
          className="grid grid-cols-12 px-4 py-2.5 text-xs border-b border-border/30 hover:bg-muted/20 items-center"
        >
          <div className="col-span-2">
            <Badge variant="info" className="text-[9px] uppercase">
              {e.eventCategory}
            </Badge>
          </div>
          <div className="col-span-3 truncate">
            <code className="font-mono text-[10px]">{e.eventType}</code>
          </div>
          <div className="col-span-1">
            {e.severity && (
              <Badge
                variant={
                  e.severity === 'critical'
                    ? 'critical'
                    : e.severity === 'high'
                      ? 'warning'
                      : 'glass'
                }
                className="text-[9px]"
              >
                {e.severity}
              </Badge>
            )}
          </div>
          <div className="col-span-2 truncate text-muted-foreground">
            <code className="font-mono text-[10px]">{e.connectorId.slice(0, 12)}…</code>
          </div>
          <div className="col-span-2">
            <Badge variant={STATUS_VARIANT[e.processingStatus] ?? 'glass'} className="text-[9px]">
              {e.processingStatus}
            </Badge>
          </div>
          <div
            className="col-span-2 text-right text-[10px] text-muted-foreground"
            title={e.createdAt}
          >
            {tsAgo(e.createdAt)}
          </div>
        </motion.div>
      ))}
    </GlassCard>
  );
}

// ─── Tab 4: Edge scanner-capability matrix ───────────────────────────

function EdgeCapabilitiesPanel(): JSX.Element {
  const [caps, setCaps] = useState<EdgeScannerCapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await getEdgeScannerCapabilities();
      setCaps(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  if (loading) return <LoadingState caption="Loading capability matrix…" />;
  if (error) return <ErrorState title="Failed to load" detail={error} onRetry={() => void refresh()} />;
  if (!caps) return <></>;

  const sections = [
    { title: 'Discovery - Scanner types', caps: caps.capabilities.discovery.scanners },
    { title: 'Discovery - Classifier tiers', caps: caps.capabilities.discovery.classifierTiers },
    { title: 'Discovery - Features', caps: caps.capabilities.discovery.features },
    { title: 'Activity - Readers', caps: caps.capabilities.activity.readers },
    {
      title: 'Detection - Sidecar rewriters',
      caps: caps.capabilities.detection.sidecarRewriters,
    },
  ];

  return (
    <div className="space-y-4">
      {sections.map((s) => (
        <GlassCard key={s.title} className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Cpu className="h-4 w-4 text-primary" /> {s.title}
            </CardTitle>
            <CardDescription>
              Static capability registry - compile-time scanners + features shipped by the
              edge binary.
            </CardDescription>
          </CardHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {s.caps.map((c) => (
              <div
                key={c.id}
                className="glass-lite rounded-md p-2.5 flex items-center justify-between text-xs"
              >
                <span className="truncate">{c.label}</span>
                <Badge variant={c.enabled ? 'success' : 'glass'} className="text-[9px]">
                  {c.enabled ? 'Enabled' : 'Opt-in'}
                </Badge>
              </div>
            ))}
          </div>
        </GlassCard>
      ))}
    </div>
  );
}

// ─── Tab 5: Tier 4 exemplar catalogue ────────────────────────────────

function Tier4ExemplarsPanel(): JSX.Element {
  const [exemplars, setExemplars] = useState<Tier4ExemplarSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await listTier4Exemplars();
      setExemplars(r.exemplars);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  if (loading) return <LoadingState caption="Loading exemplar catalogue…" />;
  if (error) return <ErrorState title="Failed to load" detail={error} onRetry={() => void refresh()} />;
  if (exemplars.length === 0) return <></>;

  return (
    <>
      <GlassCard className="p-4 mb-4">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Tier-4 pgvector classifier ships with a <strong>static catalogue</strong> of exemplar
          values per PII label - character-distribution centroids the cosine-similarity engine
          matches against. Every value is fabricated (Aadhaar uses valid Verhoeff but no real
          person, PANs are valid format only, emails use example.in). The catalogue is
          intentionally compile-time for reproducibility.
        </p>
      </GlassCard>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {exemplars.map((e, i) => (
          <motion.div
            key={e.label}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.03, 0.3) }}
          >
            <GlassCard className="p-4">
              <div className="flex items-center justify-between mb-2">
                <code className="font-mono text-sm font-semibold">{e.label}</code>
                <Badge variant="glass" className="text-[9px]">
                  {e.sampleCount} samples
                </Badge>
              </div>
              <div className="space-y-1">
                {e.examples.map((ex, j) => (
                  <code
                    key={`${e.label}-${j}`}
                    className="block font-mono text-[10px] text-muted-foreground truncate"
                  >
                    {ex}
                  </code>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>
    </>
  );
}
